import React, { FunctionComponent } from 'react';
import TextField from '@material-ui/core/TextField';
import genericInputStyles from './GenericInputStyles';
import clsx from 'clsx';
import { IconButton, InputAdornment, InputLabel, Typography, Box, useTheme } from '@material-ui/core';
import { IRawError } from '../../model/IJsonSchema';
import DateRangeIcon from '@material-ui/icons/DateRange';
import { RenderInputParams } from '@material-ui/lab';
import HelpIcon from '@material-ui/icons/Help';
import TextTooltip from '../../../../../summary/components/common/TextTooltip/TextTooltip';
import { ICheckoutTheme } from '../../../../../common/theme/ICheckoutTheme';
import Translation from '../../../../../common/components/presentation/Translation/Translation';

interface IGenericInputProps {
  label: string;
  required: boolean;
  id: string;
  value: string;
  currentError?: IRawError;
  onChange?: (e: React.ChangeEvent<{ value: string }>) => void;
  className?: string;
  inputLabel?: boolean;
  type?: string;
  datePicker?: boolean;
  toggleDatePicker?: (value: boolean) => void;
  params?: RenderInputParams;
  tooltip?: string;
  placeholder?: string;
  phoneWithPrefix?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  'data-test'?: string;
}

export const GenericInput: FunctionComponent<IGenericInputProps> = ({
  label,
  required,
  id,
  onChange,
  value,
  currentError,
  inputLabel,
  type,
  datePicker,
  toggleDatePicker,
  phoneWithPrefix,
  params,
  tooltip,
  placeholder,
  readOnly,
  disabled,
  'data-test': dataTest,
}) => {
  const styles = genericInputStyles();

  const { checkoutLite } = useTheme<ICheckoutTheme>();
  const showRequiredInPlaceholder = checkoutLite?.enquiryForm?.showRequiredInPlaceholder;
  const displayRequiredInPlaceholder =
    showRequiredInPlaceholder !== undefined ? showRequiredInPlaceholder && required : required;

  return (
    <div ref={params?.InputProps?.ref} className={styles.fieldWrapper}>
      {inputLabel !== false && (
        <Box display={'flex'}>
          <Typography component={'span'} variant={'subtitle1'}>
            <InputLabel htmlFor={id} required={required} className={styles.label} data-test={`text_input:label:${id}`}>
              {label}
            </InputLabel>
          </Typography>
          {tooltip && (
            <TextTooltip
              id={id}
              text={tooltip}
              content={
                <Box component={'span'}>
                  <HelpIcon className={styles.tooltipIcon} color="primary" viewBox="0 0 28 28" />
                </Box>
              }
            />
          )}
        </Box>
      )}
      <TextField
        data-test={dataTest || `text_input:${id}`}
        id={id}
        type={type}
        variant={'outlined'}
        className={clsx([styles.input, { [styles.inputNoLabel]: inputLabel == false }])}
        placeholder={placeholder ?? `${label}${displayRequiredInPlaceholder ? ' *' : ''}`}
        value={value ?? ''}
        error={!!currentError}
        onChange={onChange}
        disabled={disabled}
        helperText={
          currentError && (
            <Typography component={'span'} className={styles.errorText} data-test={`text_input:error:${id}`}>
              {currentError}
            </Typography>
          )
        }
        InputProps={{
          readOnly: readOnly,
          classes: {
            root: styles.root,
            focused: styles.focused,
            error: styles.inputError,
            notchedOutline: styles.notchedOutline,
            disabled: styles.disabled,
          },
          endAdornment: (
            <InputAdornment position="end">
              {datePicker && (
                <IconButton onClick={() => toggleDatePicker(true)} component="span" className={styles.dateCalendar}>
                  <DateRangeIcon fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          ),
          startAdornment: (
            <InputAdornment position="start">
              {phoneWithPrefix && (
                <span className={styles.phonePrefix}>
                  <Translation id={'trans__checkout__enquiry_form__phone_prefix'} />
                </span>
              )}
            </InputAdornment>
          ),
        }}
        FormHelperTextProps={{
          classes: {
            root: styles.helperTextError,
          },
        }}
        {...params?.inputProps}
      />
    </div>
  );
};

export default GenericInput;
